import { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { RefreshCw, Download, Smartphone, Sparkles, X } from 'lucide-react';

/**
 * AutoUpdate - Comprehensive Auto-Update System for Mobile Launch
 * 
 * Handles three types of updates:
 * 1. PWA Service Worker updates (web)
 * 2. App Store/Play Store updates (native)
 * 3. Capacitor Live Updates (OTA - instant native updates)
 */

// App version - UPDATE WITH EACH RELEASE
export const APP_VERSION = '2.0.1';
export const BUILD_NUMBER = 2;

const AutoUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateType, setUpdateType] = useState(null); // 'pwa', 'store', 'live'
  const [updateInfo, setUpdateInfo] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);

  // Check if running in Capacitor native app
  const isNativeApp = typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.();
  const platform = isNativeApp ? (window.Capacitor?.getPlatform?.() || 'web') : 'web';

  // ==========================================
  // 1. PWA Service Worker Updates
  // ==========================================
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // Listen for service worker messages (UPDATE_AVAILABLE from new SW)
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'UPDATE_AVAILABLE') {
        console.log('[AutoUpdate] Received UPDATE_AVAILABLE from SW');
        setUpdateAvailable(true);
        setUpdateType('pwa');
        setUpdateInfo({ 
          version: event.data.version || 'New version',
          changes: event.data.changes || [],
          forceRefresh: event.data.forceRefresh
        });
        
        // If force refresh is requested, auto-update after 2 seconds
        if (event.data.forceRefresh) {
          setTimeout(() => {
            window.location.reload(true);
          }, 2000);
        }
      }
    });

    navigator.serviceWorker.ready.then((registration) => {
      setSwRegistration(registration);

      // Check for waiting worker
      if (registration.waiting) {
        setUpdateAvailable(true);
        setUpdateType('pwa');
        setUpdateInfo({ version: 'New version' });
      }

      // Listen for new updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              setUpdateAvailable(true);
              setUpdateType('pwa');
              setUpdateInfo({ version: 'New version' });
            }
          });
        }
      });
    });

    // Listen for controller change
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!isUpdating) {
        window.location.reload();
      }
    });

    // Check for updates more frequently (every 5 min) to catch updates faster
    const checkInterval = setInterval(() => {
      navigator.serviceWorker.ready.then((reg) => reg.update());
    }, 5 * 60 * 1000);

    return () => clearInterval(checkInterval);
  }, [isUpdating]);

  // ==========================================
  // 2. Backend Version Check (Store Updates)
  // ==========================================
  const checkBackendVersion = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/app/version?platform=${platform}&current_version=${APP_VERSION}`,
        { cache: 'no-cache', credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.update_available) {
          // Don't override a PWA update with store update
          if (!updateAvailable || updateType === 'store') {
            setUpdateAvailable(true);
            setUpdateType(isNativeApp ? 'store' : 'pwa');
            setUpdateInfo({
              version: data.latest_version,
              changes: data.release_notes || [],
              storeUrl: data.store_url,
              forceUpdate: data.force_update
            });

            // Force update immediately if required
            if (data.force_update) {
              handleUpdate();
            }
          }
        }
      }
    } catch (error) {
      console.log('Version check failed:', error.message);
    }
  }, [platform, isNativeApp, updateAvailable, updateType]);

  // ==========================================
  // 3. Capacitor Live Updates (OTA)
  // ==========================================
  const checkLiveUpdate = useCallback(async () => {
    if (!isNativeApp) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/app/live-update?platform=${platform}&current_version=${APP_VERSION}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.update_available && data.bundle_url) {
          setUpdateAvailable(true);
          setUpdateType('live');
          setUpdateInfo({
            version: data.version,
            bundleUrl: data.bundle_url,
            checksum: data.checksum
          });
        }
      }
    } catch (error) {
      console.log('Live update check failed:', error.message);
    }
  }, [isNativeApp, platform]);

  // Initial check and periodic checks
  useEffect(() => {
    // Check immediately
    checkBackendVersion();
    if (isNativeApp) {
      checkLiveUpdate();
    }

    // Check every 4 hours
    const interval = setInterval(() => {
      checkBackendVersion();
      if (isNativeApp) {
        checkLiveUpdate();
      }
    }, 4 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkBackendVersion, checkLiveUpdate, isNativeApp]);

  // ==========================================
  // Update Handler
  // ==========================================
  const handleUpdate = async () => {
    setIsUpdating(true);

    try {
      switch (updateType) {
        case 'pwa':
          // Tell service worker to skip waiting
          if (swRegistration?.waiting) {
            swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
          } else {
            // Clear caches and reload
            if ('caches' in window) {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
            }
            window.location.reload(true);
          }
          break;

        case 'store':
          // Open app store
          const storeUrl = updateInfo?.storeUrl ||
            (platform === 'ios'
              ? 'https://apps.apple.com/app/lifeflo-ai/id0000000000'
              : 'https://play.google.com/store/apps/details?id=com.lifeflo.ai');
          
          // Use Capacitor App plugin if available, otherwise window.open
          if (window.Capacitor?.Plugins?.App) {
            await window.Capacitor.Plugins.App.openUrl({ url: storeUrl });
          } else {
            window.open(storeUrl, '_blank');
          }
          setIsUpdating(false);
          break;

        case 'live':
          // Apply Capacitor live update (only for native apps)
          try {
            // Only attempt live update in native Capacitor environment
            if (isNativeApp && updateInfo?.bundleUrl && window.Capacitor?.Plugins) {
              // Use Capacitor's plugin system if available
              const updater = window.Capacitor.Plugins.CapacitorUpdater;
              if (updater) {
                const bundle = await updater.download({
                  url: updateInfo.bundleUrl,
                  version: updateInfo.version
                });
                
                if (bundle?.id) {
                  await updater.set({ id: bundle.id });
                  window.location.reload();
                  return;
                }
              }
            }
            // Fallback: just reload for web
            window.location.reload();
          } catch (e) {
            console.error('Live update failed:', e);
            window.location.reload();
          }
          break;

        default:
          window.location.reload(true);
      }
    } catch (error) {
      console.error('Update failed:', error);
      setIsUpdating(false);
    }
  };

  // Dismiss handler (unless force update)
  const handleDismiss = () => {
    if (!updateInfo?.forceUpdate) {
      setDismissed(true);
      localStorage.setItem('update_dismissed_at', Date.now().toString());
    }
  };

  // Check if recently dismissed
  useEffect(() => {
    const dismissedAt = localStorage.getItem('update_dismissed_at');
    if (dismissedAt) {
      const hoursSinceDismiss = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSinceDismiss < 24) {
        setDismissed(true);
      } else {
        localStorage.removeItem('update_dismissed_at');
      }
    }
  }, []);

  // Don't show if no update or dismissed (unless force)
  if (!updateAvailable || (dismissed && !updateInfo?.forceUpdate)) {
    return null;
  }

  // Get appropriate icon and text
  const getUpdateContent = () => {
    switch (updateType) {
      case 'store':
        return {
          icon: Smartphone,
          title: 'App Update Available',
          description: `Version ${updateInfo?.version || 'new'} is available in the ${platform === 'ios' ? 'App Store' : 'Play Store'}`,
          buttonText: 'Open Store',
          gradient: 'from-blue-600 to-cyan-500'
        };
      case 'live':
        return {
          icon: Sparkles,
          title: 'Quick Update Ready',
          description: 'An instant update is ready to install',
          buttonText: 'Install Now',
          gradient: 'from-green-600 to-emerald-500'
        };
      default:
        return {
          icon: Download,
          title: 'Update Available',
          description: 'A new version of LifeFlo is ready',
          buttonText: 'Update Now',
          gradient: 'from-teal-600 to-cyan-500'
        };
    }
  };

  const content = getUpdateContent();
  const Icon = content.icon;
  const canDismiss = !updateInfo?.forceUpdate;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-md mx-auto animate-in slide-in-from-bottom duration-300">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${content.gradient} px-4 py-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2 text-white">
            <Icon className="w-5 h-5" />
            <span className="font-semibold text-sm">{content.title}</span>
          </div>
          {canDismiss && (
            <button
              onClick={handleDismiss}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-gray-600 text-sm mb-3">
            {content.description}
          </p>

          {/* Release notes */}
          {updateInfo?.changes && updateInfo.changes.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <p className="text-xs font-medium text-gray-500 mb-2">What&apos;s new:</p>
              <ul className="space-y-1">
                {updateInfo.changes.slice(0, 3).map((change, i) => (
                  <li key={i} className="text-xs text-gray-600 flex items-start gap-2">
                    <span className="text-teal-500">•</span>
                    {change}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Update button */}
          <Button
            onClick={handleUpdate}
            disabled={isUpdating}
            className={`w-full py-3 bg-gradient-to-r ${content.gradient} hover:opacity-90 text-white`}
          >
            {isUpdating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                {content.buttonText}
              </>
            )}
          </Button>

          {/* Dismiss option */}
          {canDismiss && (
            <button
              onClick={handleDismiss}
              className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-2 py-2"
            >
              Remind me later
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoUpdate;
